import {CircularProgress, IconButton, Rating, Stack, Tooltip} from "@mui/material";
import {Page} from "../../../types/Page";
import {Proposal} from "../../../types/Proposal";
import {deleteProposal, ProposalFilter, updateFavorite} from "../../../service/proposal.service";
import {enqueueSnackbar} from "notistack";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import {adaptData} from "../../../utils/IngestorConverters";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SpeakerNotesOutlinedIcon from "@mui/icons-material/SpeakerNotesOutlined";
import SpeakerNotesOffOutlinedIcon from "@mui/icons-material/SpeakerNotesOffOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import {Delete} from "../../common/Delete";
import React, {Dispatch} from "react";
import {useAuthStore} from "../../../store/auth.store";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {DefaultError} from "@tanstack/query-core";
import {useNavigate} from "react-router";
import {PremiumMessageElementProperties} from "../PremiumMessageElement";
import {TelegramButton} from "../TelegramButton";

interface Props {
    row: Proposal;
    filter: ProposalFilter;
    containMyPhone:boolean
    favoriteLoading: Record<string, boolean>
    setFavoriteLoading: Dispatch<React.SetStateAction<Record<string, boolean>>>
    onIdToEdit?: (id: string) => void
    setProposalToTicket: (p: Proposal) => void
    setShowPremiumMessageEl: (p: PremiumMessageElementProperties) => void
}

export function ProposalControlPanelCell({setShowPremiumMessageEl, setProposalToTicket, onIdToEdit, setFavoriteLoading, favoriteLoading, row, filter, containMyPhone}:Props) {

    const navigate = useNavigate();
    const {can, jwt, user} = useAuthStore();
    const queryClient = useQueryClient();
    const canManage = can('manage_proposals')
    const fullAccess = can('full_access')
    const canEdit = canManage
        || (user && user.id && row.createdBy === user.id)
        || containMyPhone;

    const deleteMutation = useMutation<void, DefaultError, string>({
        mutationFn: (id) => deleteProposal(id).then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["proposals"]}).catch(e => {
                enqueueSnackbar({message: "Failed to invalidate proposals query: " + e.message, variant: 'error'})
            });
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to delete proposal: " + error.message, variant: 'error'})
        }
    });


    return <Stack flexDirection={"row"}
                  alignItems={"center"}
                  flexWrap={{xs: "wrap", md: "nowrap"}}
                  gap="5px"
                  justifyContent={"space-evenly"}>
        {canManage && <TelegramButton proposal={row} callback={() => {
            queryClient.setQueryData(['proposals', filter, jwt], (oldData: Page<Proposal>) => {
                if (!oldData || !oldData.content) return oldData;
                return {
                    ...oldData,
                    content: oldData.content.map((item) =>
                        item.id === row.id
                            ? {...row, telegramSendDate: new Date().getTime()}
                            : item
                    ),
                }
            })
        }}/>}
        {favoriteLoading[row.id] && <CircularProgress size={'46px'} sx={{margin: '0 -4px'}}/>}
        {!favoriteLoading[row.id] && <IconButton sx={{margin: '0 -4px'}}>
            <Rating
                value={jwt ? (row.favorite ? 1 : 0) : 1}
                sx={{
                    "& .MuiRating-iconHover": {
                        color: "inherit !important",
                    }
                }}
                precision={1}
                size="large"
                max={1}
                onChange={(event) => {
                    if (!jwt) {
                        setShowPremiumMessageEl({
                            element: (event.target as Element),
                            isOpen: true,
                            isFavorite: false,
                            isFavoriteInTable: true,
                            isPhone: false,
                            isMy: false,
                            isSupport: false,
                            isPdf: false
                        });
                        return;
                    }

                    queryClient.setQueryData(['proposals', filter, jwt], (oldData: Page<Proposal>) => {
                        if (!oldData) return oldData;

                        return {
                            ...oldData,
                            content: oldData.content.map((item) =>
                                item.id === row.id
                                    ? {...item, favorite: !item.favorite}
                                    : item
                            )
                        }
                    });

                    setFavoriteLoading({...favoriteLoading, [row.id]: true});
                    updateFavorite(row.id).then(() => {
                        queryClient.invalidateQueries({queryKey: ["proposals"], refetchType: 'inactive'});
                    }).catch(() => {
                        enqueueSnackbar({message: "Failed to update favorites", variant: 'error'});
                    }).finally(() => {
                        setFavoriteLoading((prevState) => {
                            delete (prevState[row.id]);
                            return {...prevState}
                        })
                    })
                }}
                emptyIcon={<StarBorderOutlinedIcon style={{opacity: 0.55}} fontSize="inherit"/>}
            />
        </IconButton>}

        {fullAccess && <IconButton disabled={!row.link} href={adaptData(row.source, 'link', row.link)}
                                   target={"_blank"}>
            <OpenInNewIcon color={row.link ? "info" : "disabled"}/>
        </IconButton>}

        {canManage && row.adminNotes ? <Tooltip title={row.adminNotes}>
                <SpeakerNotesOutlinedIcon sx={{padding: '8px 4px'}} color={"success"}/>
            </Tooltip>
            : (canManage ?
                <SpeakerNotesOffOutlinedIcon color={"disabled"} sx={{padding: '8px 4px'}}/> : undefined)}

        {canEdit && <IconButton onClick={() => {
            onIdToEdit ? onIdToEdit(row.id) : navigate("/prop/" + row.id);
        }}>
            <EditNoteOutlinedIcon color={'info'}/>
        </IconButton>}

        {!canEdit &&
            <Tooltip title={"You lack the required permissions to edit this proposal"}><span><IconButton
                disabled={true}>
                        <EditNoteOutlinedIcon color={'disabled'}/>
                    </IconButton></span></Tooltip>}

        {!jwt && <IconButton onClick={(e) => setShowPremiumMessageEl({
            element: e.currentTarget,
            isOpen: true,
            isFavorite: false,
            isFavoriteInTable: false,
            isPdf: false,
            isPhone: false,
            isMy: false,
            isSupport: true,
        })}>
            <ContactSupportOutlinedIcon/>
        </IconButton>}
        {jwt && <IconButton onClick={() => setProposalToTicket(row)}>
            <ContactSupportOutlinedIcon/>
        </IconButton>}
        {canManage && <Delete onConfirm={() => {
            deleteMutation.mutate(row.id);
        }}/>}
    </Stack>
}